import { Effect } from 'effect';
import { useAsync } from './useAsync';
import { useMemo } from 'preact/hooks';

export type EffectResult<A, E> = {
    // The up-to-date result of the current Effect. Undefined while Effect has not yet completed or if it
    // completed with an error
    result?: A;
    // The error value of the current effect. Undefined while Effect has not yet completed, or if it
    // completed successfully
    error?: E;
    // The result of the most recent successful effect. Undefined until the first successful Effect completion
    // then retains the latest successful result, updating only upon later successful Effect completions.
    latest?: A;
    // Force the Effect to re-run even if none of the inputs have changed.
    retry: () => void;
};

export function useEffectful<A, E>(
    f: () => Effect.Effect<A, E, never>,
    inputs?: unknown[],
): EffectResult<A, E>;

/**
 * @deprecated use the lazy-evaluated version of this function signature
 */
export function useEffectful<A, E>(
    eff: Effect.Effect<A, E, never>,
    inputs?: unknown[],
): EffectResult<A, E>;

/**
 * Essentially an asynchronous useMemo for Effects
 * Use where you want to memoise an asynchronously computed/fetched result. This prevents
 * older results replacing later results incorrectly, and follows the same reevaluation
 * semantics as useMemo.
 *
 * Note: although stale results are suppressed, `useEffectful` is currently unable to
 * actually cancel in-flight tasks when inputs change. Using this for asynchronous side
 * effects is  therefore hazardous and not recommended. This should only be used for Effectful
 * computation and fetching, not manipulating state or other side effects.
 *
 * This is essentially the same as `useAsync`, but acting on Effects instead
 * of javascript Promises
 */
export function useEffectful<A, E>(
    f: Effect.Effect<A, E, never> | (() => Effect.Effect<A, E, never>),
    inputs?: unknown[],
): EffectResult<A, E> {
    const eff = useMemo(() => (typeof f === 'function' ? f() : f), inputs);

    // As useAsync already implements all the state tracking, etc, we need,
    // just run the Effect as a Promise and handle it via useAsync.
    // This gives us no opportunity to handle Effect cancellation which may
    // be desirable in future.
    const { result, latest, error, retry } = useAsync(() => Effect.runPromise(eff), [eff]);

    return {
        result,
        // Running the effect as a promise loses Effect's strong typing of the error value.
        // Since running the effect is the only fallible operation in our `useAsync` above
        // we can thus safely cast any error value to the type Effect declares.
        error: error as E | undefined,
        latest,
        retry,
    };
}
